import React from 'react';
import { motion } from 'framer-motion';
import HandwrittenChecklist from './HandwrittenChecklist';
import StickyNote from './StickyNote';
import ScribbleUnderline from './ScribbleUnderline';

const workLog = [
  { time: '7:02 AM', items: [
    { text: 'Sorted 47 emails — 3 need you', done: true },
    { text: 'Drafted reply to Marcus re: SOW', done: true },
    { text: 'Flagged AWS invoice → accounting', done: true },
  ]},
  { time: '8:15 AM', items: [
    { text: 'Moved Sarah → Thu 10AM', done: true },
    { text: 'Deep Work block: intact ✓', done: true },
    { text: 'Pre-read drafted for Sarah sync', done: true },
  ]},
  { time: '9:50 AM', items: [
    { text: 'Northgate brief compiled', done: true },
    { text: 'Pulled 2 mutual connections', done: true },
    { text: 'Sent 3-bullet summary to Telegram', done: true },
  ]},
  { time: '2:30 PM', items: [
    { text: 'Delta kickoff: brief attached to invite', done: true },
    { text: 'Post-meeting template created', done: true },
    { text: 'Q4 offsite venues → Notion doc', done: false },
  ]},
];

const NotebookWorkLog = () => (
  <section className="py-24 relative overflow-hidden">
    {/* Notebook background */}
    <div className="absolute inset-0 notebook-bg" />
    <div className="absolute top-0 bottom-0 left-[88px] w-px bg-[rgba(220,80,80,0.18)] hidden lg:block" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-12 items-start">

        {/* Left: heading */}
        <div className="lg:col-span-4 lg:pl-16">
          <div className="tag-pill mb-5">Today's Work Log</div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A1A18] mb-3 leading-tight tracking-tight"
            style={{ letterSpacing: '-0.02em' }}>
            What your assistant<br />
            <span className="gradient-text-accent">did while you slept.</span>
          </h2>
          <ScribbleUnderline color="rgba(42,92,138,0.25)" width={200} />
          <p className="text-[#6B6560] text-sm leading-relaxed mt-4 mb-8">
            This is a real snapshot of the kind of work WeekOne handles before 10AM. No prompts. No instructions. Just context.
          </p>

          {/* Margin annotations */}
          <div className="space-y-6 pl-4 border-l-2 border-[rgba(42,92,138,0.15)]">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <p className="font-handwritten text-lg text-[#2A5C8A]" style={{ transform: 'rotate(-1deg)' }}>
                ← ran without a single prompt
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }}>
              <p className="font-handwritten text-base text-[#3D7A5E]" style={{ transform: 'rotate(1.5deg)' }}>
                saved ~2.5 hrs this morning alone
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.6 }}>
              <p className="font-handwritten text-base text-[#8A6A2A]" style={{ transform: 'rotate(-1deg)' }}>
                * still working on the Notion doc ↓
              </p>
            </motion.div>
          </div>
        </div>

        {/* Right: work log cards */}
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
          {workLog.map((block, i) => (
            <motion.div key={block.time}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="bg-white border border-[#E4DFD8] rounded-xl p-5 relative overflow-hidden shadow-sm"
            >
              {/* Ruled lines behind content */}
              <div className="absolute inset-0 ruled-lines opacity-40 pointer-events-none rounded-xl" />
              <div className="relative">
                <div className="font-mono-custom text-[10px] text-[#9E9690] uppercase tracking-widest mb-3">{block.time}</div>
                <HandwrittenChecklist items={block.items} delay={i * 0.1} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Bottom sticky notes strip */}
      <div className="mt-16 flex flex-wrap justify-center gap-8 items-start">
        <StickyNote color="yellow" rotate="-2deg" delay={0.1}>
          <p className="text-sm font-bold mb-1">This week's summary</p>
          <p className="text-xs">📧 312 emails handled</p>
          <p className="text-xs">📅 18 meetings scheduled</p>
          <p className="text-xs">⏱ ~8.5 hrs saved</p>
          <p className="text-xs text-[#9E9690] mt-1 font-semibold">by WeekOne</p>
        </StickyNote>
        <StickyNote color="green" rotate="1.5deg" delay={0.2}>
          <p className="text-sm font-bold mb-1">Tone calibrated ✓</p>
          <p className="text-xs">Replies now match</p>
          <p className="text-xs">your writing style</p>
          <p className="text-xs text-[#14532D] mt-1">Day 7 milestone</p>
        </StickyNote>
        <StickyNote color="blue" rotate="-1deg" delay={0.3}>
          <p className="text-sm font-bold mb-1">Next: CRM sync</p>
          <p className="text-xs">HubSpot pipeline</p>
          <p className="text-xs">updated after calls</p>
          <p className="text-xs text-[#1E4D7A] mt-1">Pro plan feature</p>
        </StickyNote>
        <StickyNote color="pink" rotate="2.5deg" delay={0.4}>
          <p className="text-sm font-bold mb-1">Reminder set 🔔</p>
          <p className="text-xs">Marcus follow-up</p>
          <p className="text-xs">Thursday 9AM</p>
          <p className="text-xs text-[#881337] mt-1">auto-queued</p>
        </StickyNote>
      </div>
    </div>
  </section>
);

export default NotebookWorkLog;